// Bucket-code -> Round-7 user-facing label mapping for AP Purchase Invoice.
//
// "Round-7" refers to the Phase 5 Round 7 cleanup that gave Finance shorter,
// human-readable status strings (see automations/ap-purchase-invoice/README.md
// and docs/erp-learnings/retool-save-977-inventory.md §3). Retool's
// `getPending` SQL CASE has been driving the queue for a week — these are the
// strings Finance has been reading. We mirror them verbatim where the SQL
// CASE keys exist, and add the canonical / short-form keys (the ones the live
// N8N inbox writes into `payload.error_type`) on top of that.
//
// Keep entries stable: the same code must always render the same label.

export const BUCKET_LABELS: Record<string, string> = {
  // --- Canonical / short bucket keys (live inbox writes these) ---
  MULTIPLE_GOODS_RECEIPTS: 'Multiple Goods Receipts',
  SUPPLIER_AMBIGUOUS: 'Supplier Ambiguous',
  ALREADY_INVOICED: 'Already Invoiced',
  PURCHASE_ORDER_AMOUNT_MISMATCH: 'Purchase Order Amount Mismatch',
  GOODS_RECEIPT_REVIEW: 'Goods Receipt Review',
  GRN_UNAPPROVED: 'GRN awaiting approval',
  PARSE_ERROR: 'Could not read invoice',
  EXP_REB_REVIEW: 'Exp/Reb review',
  MIXED_PI_BOOK_CONTEXT: 'Goods posting context issue',
  GRN_DETAIL_SPLIT_UNSUPPORTED: 'GRN split unsupported',
  TAX_MISMATCH_REVIEW: 'Tax review',
  DATE_REVIEW: 'Invoice date review',

  // --- Long-form bucket keys from the Round-7 Retool CASE ---
  // (See retool-save-977-inventory.md §3 — these are the literal strings
  //  that Retool's getPending CASE returns for each error_type.)
  AMOUNT_ONLY_GRN_MATCH_NEEDS_REVIEW: 'GRN amount match needs review',
  NO_GRN_FOUND: 'No GRN found',
  GRN_NOT_FOUND: 'No GRN found',
  PO_GRN_AMOUNT_MISMATCH_NEEDS_REVIEW: 'PO / GRN amount mismatch',
  SUPPLIER_NOT_FOUND: 'Supplier not found',
  SUPPLIER_AMBIGUOUS_NEEDS_REVIEW: 'Supplier Ambiguous',
  GRN_ALREADY_INVOICED_NEEDS_REVIEW: 'Already Invoiced',
  MULTIPLE_GRN_TOTAL_MATCH_AMBIGUOUS: 'Multiple Goods Receipts',
  MULTIPLE_GRN_TOTAL_MATCH_NEEDS_REVIEW: 'Multiple Goods Receipts',
  MULTIPLE_GRN_FOUND: 'Multiple Goods Receipts',
  SINGLE_GRN_TOTAL_MATCH_NEEDS_REVIEW: 'Goods Receipt Review',
  SUPPLIER_OCR_CORRECTED_GRN_AMOUNT_REVIEW: 'Supplier OCR Corrected — Review',
  NO_POSTABLE_GRN_FOUND: 'No Postable GRN Found',

  // --- Status / lifecycle ---
  DUPLICATE: 'Duplicate invoice',
  POSTED: 'Posted',
  PENDING_APPROVAL: 'Ready to approve',
  ERROR: 'Error',
  REJECTED: 'Rejected',
  SERVICE_PREFLIGHT_INCOMPLETE: 'Service preflight incomplete',
  SERVICE_MANUAL_PREP_SAVED: 'Manual service prep saved',
};

const EM_DASH = '—';

function titleCaseFromCode(code: string): string {
  // Convert SCREAMING_SNAKE_CASE / kebab-case / mixed -> Title Case words.
  const cleaned = code
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!cleaned) return EM_DASH;
  return cleaned
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function humanizeBucket(code: string | null | undefined): string {
  if (code === null || code === undefined) return EM_DASH;
  const trimmed = String(code).trim();
  if (!trimmed) return EM_DASH;
  const upper = trimmed.toUpperCase();
  if (upper in BUCKET_LABELS) return BUCKET_LABELS[upper];
  return titleCaseFromCode(trimmed);
}

// Pull the rich, sentence-length action message for a row.
// Order: explicit error_message > recommended_action > humanised bucket.
// `payload.action_message` is intentionally NOT consulted — it's absent on
// every migrated/pending row in production (see ap-payload-contract.md §7.3).
export function actionMessage(
  payload: Record<string, unknown> | null | undefined,
): string {
  if (!payload) return EM_DASH;

  const errorMessage = payload['error_message'];
  if (typeof errorMessage === 'string' && errorMessage.trim()) {
    return errorMessage.trim();
  }

  const recommended = payload['recommended_action'];
  if (typeof recommended === 'string' && recommended.trim()) {
    return recommended.trim();
  }

  const override = payload['status_label_override'];
  if (typeof override === 'string' && override.trim()) {
    return override.trim();
  }

  const errorType = payload['error_type'];
  if (typeof errorType === 'string' && errorType.trim()) {
    return humanizeBucket(errorType);
  }

  const oracleClassification = payload['oracle_classification'];
  if (typeof oracleClassification === 'string' && oracleClassification.trim()) {
    return humanizeBucket(oracleClassification);
  }

  return EM_DASH;
}
